import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  LinearProgress,
  Grid,
  Paper,
  Collapse,
  Dialog,
  Button
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { TbHazeMoon } from "react-icons/tb";
import { PiPathBold } from "react-icons/pi";
import { MdStorm, MdOutlineVpnKey, MdExpandMore, MdExpandLess } from "react-icons/md";
import { LuBrain } from "react-icons/lu";
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { getSerenityColor } from '../../utils/serenityColors';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { deleteImageRecord, type ImageDoc, updateImageFavorite } from '../../services/gallery';
import { deleteImage } from '../../services/images';

interface DreamDetailProps {
  dream: {
    id: string;
    text: string;
    emotions: string;
    analysis: string;
    keywords: string[];
    serenityScore: number;
    createdAt: Date;
    approaches: Array<{
      categoryName: string;
      subcategoryName: string;
    }>;
  };
  onDelete: (id: string) => void;
  image: ImageDoc | null;
  onImageUpdated: (dreamId: string, image: ImageDoc | null) => void;
}

export default function DreamDetail({ dream, onDelete, image, onImageUpdated }: DreamDetailProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [expandedDream, setExpandedDream] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);
  const { t, i18n } = useTranslation();

  const getLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'es':
        return es;
      default:
        return enUS;
    }
  };

  const formatDate = (date: Date) => {
    return format(date, t('dreamJournal.dreamDetail.dateFormat'), {
      locale: getLocale()
    });
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleDreamExpandClick = () => {
    setExpandedDream(!expandedDream);
  };

  const handleToggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    if (next === false) {
      setExpanded(true);
      setExpandedDream(true);
    }
  };

  const handleOpenPreview = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!image) return;
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const toggleFavorite = async () => {
    if (!image || isUpdatingFavorite) return;
    setIsUpdatingFavorite(true);
    try {
      await updateImageFavorite(image.id, !image.favorite);
      onImageUpdated(dream.id, { ...image, favorite: !image.favorite });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du favori:', error);
    } finally {
      setIsUpdatingFavorite(false);
    }
  };

  const openDeleteConfirmation = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteImage = async () => {
    if (!image) return;
    try {
      closeDeleteConfirmation();
      setPreviewOpen(false);
      if (image.fileId || image.filePath || image.url) {
        await deleteImage({ fileId: image.fileId || undefined, filePath: image.filePath || undefined, url: image.url });
      }
      await deleteImageRecord(image.id);
      onImageUpdated(dream.id, null);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'image:', error);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2, md: 3 },
        background: 'rgba(30, 32, 69, 0.9)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        minHeight: '100%'
      }}
    >
      {/* En-tête avec date et bouton supprimer - Cliquable pour déplier/replier */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: collapsed ? 0 : 3,
        pb: 2,
        borderBottom: '1px solid rgba(104, 241, 209, 0.4)',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(104, 241, 209, 0.0)'
        }
      }}
      onClick={handleToggleCollapsed}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon sx={{ color: 'var(--light-green)' }} />
          <Typography variant="h5" component="h2" sx={{ 
            color: 'var(--primary-color)',
            fontWeight: 300,
            letterSpacing: '0.5px'             
          }}>
            {formatDate(dream.createdAt)} 
          </Typography>
          <Typography variant="body1" sx={{ 
              mb: 0,
              color: 'var(--light-green)',
              display: 'flex'
            }}>
                 / {t('dreamJournal.dreamDetail.lastRecordedDream')}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Icône expand/collapse */}
          <IconButton 
            sx={{ 
              color: 'var(--light-green)',
              p: 0.5,
              transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s'
            }}
          >
            <MdExpandMore size="1.2rem" />
          </IconButton>
          {/* Bouton supprimer */}
          <IconButton 
            onClick={(e) => {
              e.stopPropagation(); // Empêche le toggle lors du clic sur supprimer
              onDelete(dream.id);
            }} 
            sx={{ 
              color: 'rgba(212, 195, 255, 0.9)',
              '&:hover': {
                color: '#ff6363',
                backgroundColor: 'rgba(255, 99, 99, 0.1)'
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Contenu repliable -----------------------------------------------------*/}
      <Collapse in={!collapsed} timeout={300}>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            {/* ==== PREMIÈRE LIGNE ==================================== */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={6}
                sx={{
                  p: 1,
                  mb: 1,
                  background: 'var(--fdDreamContent)',
                  borderRadius: '15px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                  position: 'relative',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'black', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TbHazeMoon size="1.3rem" /> {t('dreamJournal.dreamDetail.dream')}
                  </Typography>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDreamExpandClick();
                    }}
                    sx={{
                      p: 0,
                      color: 'black',
                      transform: expandedDream ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s'
                    }}
                  >
                    <MdExpandMore size="1.3rem" />
                  </IconButton>
                </Box>
                <Box sx={{ position: 'relative', mx: -2, mb: -2, mt: 0 }}>
                  <Collapse in={expandedDream} collapsedSize={145}>
                    <Box sx={{ px: { xs: 2, sm: 3, md: 3 }, pb: expandedDream ? 3 : 0 }}>
                      <Typography
                        sx={{
                          color: 'black',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.4,
                          fontSize: '0.9rem'
                        }}
                      >
                        {dream.text}
                      </Typography>
                    </Box>
                  </Collapse>
                  {!expandedDream && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 8,
                        right: 8,
                        height: '40px',
                        opacity: 0.7,
                        pointerEvents: 'none',
                        borderRadius: '0 0 10px 10px'
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper
                elevation={6}
                sx={{
                  p: 1,
                  height: '100%',
                  background: 'var(--CarteBlueSmbrUnies)',
                  borderRadius: '15px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.21)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {image ? (
                  <Box
                    onClick={handleOpenPreview}
                    sx={{
                      width: 180,
                      height: 180,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '2px solid rgba(255,255,255,0.2)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.4)'
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.name || 'dream-thumbnail'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '12px',
                      border: '2px dashed rgba(255,255,255,0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.65)',
                      fontSize: '0.75rem',
                      textAlign: 'center',
                      px: 1
                    }}
                  >
                    {t('dreamJournal.dreamDetail.noImageForDream')}
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper
                elevation={6}
                sx={{
                  p: { xs: 1, sm: 2, md: 2 },
                  height: '100%',
                  background: 'var(--CarteBlueSmbrUnies)',
                  borderRadius: '15px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Typography variant="h6" sx={{ mb: 0, color: 'var(--light-green)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SelfImprovementIcon sx={{ color: 'var(--light-green)' }} /> {t('dreamJournal.dreamDetail.serenityLevel')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography sx={{ color: getSerenityColor(dream.serenityScore), fontWeight: 'bold', fontSize: '1rem' }}>
                    {dream.serenityScore}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dream.serenityScore}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': { backgroundColor: getSerenityColor(dream.serenityScore) },
                    borderRadius: '5px',
                    height: '10px'
                  }}
                />
              </Paper>
            </Grid>

            {/* ==== DEUXIÈME LIGNE ==== */}
            <Grid item xs={12} md={3}>
              <Paper
                elevation={6}
                sx={{
                  p: { xs: 1, sm: 1, md: 1 },
                  height: '100%',
                  background: 'var(--CarteBlueSmbrUnies)',
                  borderRadius: '15px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, color: 'var(--light-green)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MdStorm size="1.3rem" /> {t('dreamJournal.dreamDetail.emotions')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {dream.emotions && dream.emotions.split(',').map((emotion, index) => (
                    <Chip
                      key={index}
                      label={emotion.trim()}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(104, 241, 209, 0.1)',
                        color: 'var(--light-green)',
                        '&:hover': { backgroundColor: 'rgba(104, 241, 209, 0.2)' }
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper
                elevation={6}
                sx={{
                  p: { xs: 1, sm: 1, md: 1 },
                  height: '100%',
                  background: 'var(--CarteBlueSmbrUnies)',
                  borderRadius: '15px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, color: 'var(--light-green)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MdOutlineVpnKey size="1.3rem" /> {t('dreamJournal.dreamDetail.keywords')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {dream.keywords.map((keyword, index) => (
                    <Chip
                      key={index}
                      label={keyword}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(104, 241, 209, 0.1)',
                        color: 'var(--light-green)',
                        '&:hover': { backgroundColor: 'rgba(104, 241, 209, 0.2)' }
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper
                elevation={6}
                sx={{
                  p: { xs: 1, sm: 1, md: 1 },
                  height: '100%',
                  background: 'var(--CarteBlueSmbrUnies)',
                  borderRadius: '15px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, color: 'var(--light-green)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PiPathBold size="1.3rem" /> {t('dreamJournal.dreamDetail.approaches')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {dream.approaches?.map((approach, index) => (
                    <Chip
                      key={index}
                      label={`${approach.categoryName} - ${approach.subcategoryName}`}
                      sx={{
                        backgroundColor: 'rgba(104, 241, 209, 0.1)',
                        color: 'var(--light-green)',
                        '&:hover': { backgroundColor: 'rgba(104, 241, 209, 0.2)' }
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* ==== QUATRIÈME LIGNE ==== */}
            <Grid item xs={12}>
              <Paper
                elevation={6}
                sx={{
                  p: 2,
                  background: 'var(--analyz-background)',
                  borderRadius: '15px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: 'black', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LuBrain size="1.3rem" /> {t('dreamJournal.dreamDetail.analysis')}
                  </Typography>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      handleExpandClick();
                    }}
                    sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s', color: 'black' }}
                  >
                    {expanded ? <MdExpandLess size="1.3rem" /> : <MdExpandMore size="1.3rem" />}
                  </IconButton>
                </Box>
                <Box sx={{ position: 'relative', mx: -2, mb: -2, mt: -1 }}>
                  <Collapse in={expanded} collapsedSize={100}>
                    <Box sx={{ px: 2, pb: expanded ? 3 : 0 }}>
                      <Box
                        sx={{
                          columnCount: { xs: 1, sm: 2, md: 3 },
                          columnGap: '2rem',
                          columnRule: '1px solid rgba(197, 0, 0, 0.47)'
                        }}
                      >
                        <Typography
                          sx={{
                            color: 'black',
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.9rem',
                            lineHeight: 1.6,
                            mb: 5
                          }}
                        >
                          {dream.analysis.split('\n\n').slice(0, -1).join('\n\n')}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'white',
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.9rem',
                            lineHeight: 1.6,
                            fontStyle: 'italic',
                            mb: 10
                          }}
                        >
                          {dream.analysis.split('\n\n').slice(-1)[0]}
                        </Typography>
                      </Box>
                    </Box>
                  </Collapse>
                  {!expanded && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '70px',
                        background: 'transparent',
                        opacity: 0.7,
                        pointerEvents: 'none',
                        borderRadius: '0 0 15px 15px'
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      <Dialog
        open={previewOpen && !!image}
        onClose={handleClosePreview}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0.3, sm: 0.5, md: 0.8, lg: 1 },
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'min(1024px, 100vw)',
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        {image && (
          <Box
            sx={{
              position: 'relative',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'stretch' },
              gap: { xs: 2, sm: 2.5, md: 3 },
              p: { xs: 2, sm: 2.5, md: 3 }
            }}
          >
            <Box
              sx={{
                order: { xs: 1, md: 2 },
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 72 },
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                justifyContent: { xs: 'flex-end', md: 'flex-start' },
                alignItems: { xs: 'center', md: 'flex-end' },
                gap: 1,
                pr: { xs: 0.5, sm: 1, md: 0 }
              }}
            >
              <IconButton
                onClick={handleClosePreview}
                sx={{ color: 'white', backgroundColor: 'var(--home-subtitle)', '&:hover': { backgroundColor: 'var(--primary-color)' } }}
                aria-label={t('common.close')}
              >
                <CloseIcon />
              </IconButton>

              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  toggleFavorite();
                }}
                disabled={isUpdatingFavorite}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: image.favorite ? 'var(--red)' : 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' }
                }}
                aria-label={image.favorite ? t('gallery.controls.tooltipRemoveFavorite') : t('gallery.controls.tooltipAddFavorite')}
              >
                {image.favorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
              </IconButton>

              <IconButton
                onClick={(event) => openDeleteConfirmation(event)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' }
                }}
                aria-label={t('gallery.controls.tooltipDelete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                order: { xs: 2, md: 1 },
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pb: { xs: 2, sm: 2, md: 0 }
              }}
            >
              <img
                src={image.url}
                alt={image.name || 'preview'}
                style={{ width: '100%', height: '100%', objectFit: 'contain', maxWidth: '1024px', maxHeight: '100vh' }}
              />
            </Box>

            <Box sx={{ order: 3, display: { xs: 'block', md: 'none' }, width: '100%', height: 48 }} />
          </Box>
        )}
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteConfirmation}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(12, 22, 48, 0.98)',
            borderRadius: 0.5,
            boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
            px: 3,
            py: 2.5,
            minWidth: 280
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ fontFamily: 'Comfortaa', color: 'white', fontWeight: 600 }}>
          {t('gallery.controls.deleteConfirmTitle')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.75)' }}>
          {t('gallery.controls.deleteConfirmDescription')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255,255,255,0.6)' }}>
          {t('gallery.controls.deleteConfirmDreamRetention')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={handleDeleteImage} sx={{ color: 'var(--primary-color)' }}>
            {t('gallery.controls.deleteButton')}
          </Button>
          <Button onClick={closeDeleteConfirmation} sx={{ color: 'rgba(255,255,255,0.75)' }}>
            {t('gallery.controls.cancelButton')}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
